// Session 38 Phase 2 · Admin → Inventory → Workspaces table
// Mirrors AdminInventoryVenues.jsx but scopes to child workspaces + surfaces
// parent venue on every row (per the "location belongs to parent" invariant).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";

function StatusBadge({ published, status }) {
  const state = status === "archived" ? "archived"
              : status === "paused" ? "paused"
              : status === "draft" ? "draft"
              : published ? "published" : "unpublished";
  const style = {
    display: "inline-flex", padding: "3px 10px", fontSize: 11,
    letterSpacing: "0.14em", textTransform: "uppercase", borderRadius: 999,
    background: state === "published" ? "rgba(20,120,60,0.12)"
              : state === "archived"  ? "rgba(80,80,80,0.14)"
              : "rgba(200,120,20,0.14)",
    color: state === "published" ? "#146F3B"
         : state === "archived"  ? "#4b4b4b"
         : "#8A5A00",
    border: state === "published" ? "1px solid rgba(20,120,60,0.18)"
          : state === "archived"  ? "1px solid rgba(80,80,80,0.20)"
          : "1px solid rgba(200,120,20,0.24)",
  };
  return <span style={style} data-testid={`workspace-status-${state}`}>{state}</span>;
}

function fmtMoney(v) {
  if (v == null || v === 0) return "—";
  return `$${Number(v).toFixed(v >= 100 ? 0 : 2)}`;
}

export default function AdminInventoryWorkspaces() {
  const loc = useLocation();
  const initialVenueId = new URLSearchParams(loc.search).get("venue_id") || "";
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [wsType, setWsType] = useState("");
  const [published, setPublished] = useState("");
  const [venueId, setVenueId] = useState(initialVenueId);
  const [openRow, setOpenRow] = useState(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (city.trim()) p.set("city", city.trim());
    if (state.trim()) p.set("state", state.trim());
    if (postalCode.trim()) p.set("postal_code", postalCode.trim());
    if (wsType) p.set("workspace_type", wsType);
    if (published) p.set("published", published);
    if (venueId) p.set("venue_id", venueId);
    p.set("limit", "300");
    return p.toString();
  }, [q, city, state, postalCode, wsType, published, venueId]);

  // Session 59.5 · P0 · Stable callback for exhaustive-deps.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/workspaces?${params}`);
      setRows(r.data.workspaces || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load workspaces");
    } finally {
      setLoading(false);
    }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const togglePublish = async (w) => {
    try {
      await api.put(`/admin/workspaces/${w.workspace_id}`, { published: !w.published });
      toast.success(w.published ? "Unpublished" : "Published");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const archive = async (w) => {
    if (!window.confirm(`Archive "${w.name}"? Bookings will be preserved.`)) return;
    try {
      await api.put(`/admin/workspaces/${w.workspace_id}`, { status: "archived" });
      toast.success("Archived");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    }
  };

  const clearFilters = () => {
    setQ(""); setCity(""); setState(""); setPostalCode("");
    setWsType(""); setPublished(""); setVenueId("");
  };

  return (
    <AppShell>
      <div className="m-container" style={{ paddingTop: 40, paddingBottom: 80 }}>
        <div style={{ marginBottom: 6, letterSpacing: "0.18em", fontSize: 11,
                       textTransform: "uppercase", color: "rgba(27,35,64,0.55)" }}>
          Admin · Inventory
        </div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between",
                       gap: 20, marginBottom: 24 }}>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 40, margin: 0,
                        color: "var(--m-navy)" }}>
            Workspaces
          </h1>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Link to="/admin/inventory/venues" className="m-btn-ghost"
                   data-testid="admin-nav-venues"
                   style={{ fontSize: 13, padding: "8px 14px" }}>
              ← Venues
            </Link>
            <span style={{ color: "rgba(27,35,64,0.55)", fontSize: 13 }}>
              {loading ? "Loading…" : `${total} workspace${total === 1 ? "" : "s"}`}
            </span>
          </div>
        </div>

        {venueId && (
          <div style={{ padding: "10px 14px", background: "rgba(184,145,58,0.10)",
                         border: "1px solid rgba(184,145,58,0.24)", borderRadius: 8,
                         marginBottom: 16, fontSize: 13, display: "flex",
                         justifyContent: "space-between", alignItems: "center" }}
                data-testid="admin-workspaces-venue-filter-banner">
            <span>Filtered to venue <code>{venueId}</code></span>
            <button onClick={() => setVenueId("")} className="m-btn-ghost"
                     style={{ fontSize: 12, padding: "4px 10px" }}
                     data-testid="admin-workspaces-clear-venue-filter">Clear</button>
          </div>
        )}

        <div style={{ display: "grid",
                       gridTemplateColumns: "1.4fr 1fr 90px 100px 160px 130px auto",
                       gap: 10, alignItems: "end", marginBottom: 22,
                       padding: "16px 18px", background: "rgba(27,35,64,0.03)",
                       border: "1px solid rgba(27,35,64,0.08)", borderRadius: 10 }}>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                             color: "rgba(27,35,64,0.55)" }}>Search</span>
            <input value={q} onChange={e => setQ(e.target.value)}
                    placeholder="name, city, zip…"
                    data-testid="admin-workspaces-search"
                    style={{ padding: "8px 10px" }} />
          </label>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                             color: "rgba(27,35,64,0.55)" }}>City</span>
            <input value={city} onChange={e => setCity(e.target.value)}
                    data-testid="admin-workspaces-city" style={{ padding: "8px 10px" }} />
          </label>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                             color: "rgba(27,35,64,0.55)" }}>State</span>
            <input value={state} onChange={e => setState(e.target.value)}
                    placeholder="NH"
                    data-testid="admin-workspaces-state" style={{ padding: "8px 10px" }} />
          </label>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                             color: "rgba(27,35,64,0.55)" }}>ZIP</span>
            <input value={postalCode} onChange={e => setPostalCode(e.target.value)}
                    placeholder="03110"
                    data-testid="admin-workspaces-zip" style={{ padding: "8px 10px" }} />
          </label>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                             color: "rgba(27,35,64,0.55)" }}>Type</span>
            <select value={wsType} onChange={e => setWsType(e.target.value)}
                     data-testid="admin-workspaces-type"
                     style={{ padding: "8px 10px" }}>
              <option value="">Any</option>
              <option>Coworking Space</option>
              <option>Event Venue</option>
              <option>Meeting Room</option>
              <option>Private Office</option>
            </select>
          </label>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                             color: "rgba(27,35,64,0.55)" }}>Published</span>
            <select value={published} onChange={e => setPublished(e.target.value)}
                     data-testid="admin-workspaces-published"
                     style={{ padding: "8px 10px" }}>
              <option value="">Any</option>
              <option value="true">Published</option>
              <option value="false">Unpublished / drafts</option>
            </select>
          </label>
          <button type="button" onClick={clearFilters} className="m-btn-ghost"
                   style={{ padding: "9px 14px", fontSize: 12, whiteSpace: "nowrap" }}
                   data-testid="admin-workspaces-clear-filters">
            Clear
          </button>
        </div>

        <div style={{ overflowX: "auto", border: "1px solid rgba(27,35,64,0.10)",
                       borderRadius: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}
                  data-testid="admin-workspaces-table">
            <thead>
              <tr style={{ background: "rgba(27,35,64,0.04)",
                            borderBottom: "1px solid rgba(27,35,64,0.10)" }}>
                {["Workspace", "Parent venue", "City", "State", "ZIP",
                   "Type", "Pricing", "Published", "Updated", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 14px", textAlign: "left",
                                         fontWeight: 600, fontSize: 11,
                                         letterSpacing: "0.14em",
                                         textTransform: "uppercase",
                                         color: "rgba(27,35,64,0.62)" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={10} style={{ padding: "40px 20px", textAlign: "center",
                                              color: "rgba(27,35,64,0.55)" }}>
                    No workspaces match your filters.
                  </td>
                </tr>
              )}
              {/* Session 60.19 · Alphabetize workspaces by name for consistent admin scan. */}
              {[...rows].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map(w => (
                <tr key={w.workspace_id}
                     data-testid={`admin-workspaces-row-${w.workspace_id}`}
                     style={{ borderTop: "1px solid rgba(27,35,64,0.06)" }}>
                  <td style={{ padding: "12px 14px" }}>
                    {/* Session 42 · Workspace name is a direct link to its
                        Edit page (matches new venues-table UX). */}
                    <div style={{ fontWeight: 600, color: "var(--m-navy)" }}
                         data-testid={`admin-workspaces-name-${w.workspace_id}`}>
                      <Link to={`/admin/workspaces/${w.workspace_id}/edit`}
                             data-testid={`admin-workspaces-name-link-${w.workspace_id}`}>
                        {w.name}
                      </Link>
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(27,35,64,0.55)" }}>
                      {w.slug}
                    </div>
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 13 }}>
                    {w.parent_venue ? (
                      <>
                        <Link to={`/admin/venues/${w.parent_venue.venue_id}/edit`}
                               data-testid={`admin-workspaces-parent-link-${w.workspace_id}`}>
                          {w.parent_venue.name}
                        </Link>
                      </>
                    ) : <span style={{color:"#aaa"}}>—</span>}
                  </td>
                  <td style={{ padding: "12px 14px" }}>{w.parent_venue?.city || "—"}</td>
                  <td style={{ padding: "12px 14px" }}>{w.parent_venue?.state || "—"}</td>
                  <td style={{ padding: "12px 14px", fontFamily: "monospace" }}>
                    {w.parent_venue?.postal_code || "—"}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 13 }}>{w.workspace_type || "—"}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13 }}>
                    {fmtMoney(w.hourly)}<span style={{color:"#aaa"}}>/hr</span>
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <StatusBadge published={w.published} status={w.status} />
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 12,
                                color: "rgba(27,35,64,0.55)" }}>
                    {w.updated_at ? new Date(w.updated_at).toLocaleDateString() : "—"}
                  </td>
                  <td style={{ padding: "8px 8px", position: "relative" }}>
                    <button
                      onClick={() => setOpenRow(openRow === w.workspace_id ? null : w.workspace_id)}
                      className="m-btn-ghost"
                      style={{ fontSize: 12, padding: "6px 10px" }}
                      data-testid={`admin-workspaces-actions-${w.workspace_id}`}
                    >
                      Actions ▾
                    </button>
                    {openRow === w.workspace_id && (
                      <div style={{ position: "absolute", top: "100%", right: 8, zIndex: 20,
                                     background: "#fff", boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
                                     border: "1px solid rgba(27,35,64,0.10)",
                                     borderRadius: 8, minWidth: 220, padding: "6px 0" }}>
                        <ActionItem to={`/spaces/${w.slug}`} newTab
                                     testid={`admin-workspaces-open-${w.workspace_id}`}>
                          Open workspace
                        </ActionItem>
                        <ActionItem to={`/admin/workspaces/${w.workspace_id}/edit`}
                                     testid={`admin-workspaces-edit-${w.workspace_id}`}>
                          Edit workspace
                        </ActionItem>
                        {w.parent_venue && (
                          <ActionItem to={`/admin/venues/${w.parent_venue.venue_id}/edit`}
                                       testid={`admin-workspaces-view-parent-${w.workspace_id}`}>
                            View parent venue
                          </ActionItem>
                        )}
                        <hr style={{ margin: "6px 0", border: 0,
                                      borderTop: "1px solid rgba(27,35,64,0.08)" }} />
                        {w.published ? (
                          <ActionItem onClick={() => { setOpenRow(null); togglePublish(w); }}
                                       testid={`admin-workspaces-unpublish-${w.workspace_id}`}>
                            Unpublish
                          </ActionItem>
                        ) : (
                          <ActionItem onClick={() => { setOpenRow(null); togglePublish(w); }}
                                       testid={`admin-workspaces-publish-${w.workspace_id}`}>
                            Publish
                          </ActionItem>
                        )}
                        <hr style={{ margin: "6px 0", border: 0,
                                      borderTop: "1px solid rgba(27,35,64,0.08)" }} />
                        <ActionItem danger onClick={() => { setOpenRow(null); archive(w); }}
                                     testid={`admin-workspaces-archive-${w.workspace_id}`}>
                          Archive workspace
                        </ActionItem>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p style={{ marginTop: 24, fontSize: 12, color: "rgba(27,35,64,0.55)",
                     lineHeight: 1.7 }}>
          Workspaces inherit their address from the parent venue. Search by city,
          state, or ZIP matches on the parent venue&apos;s location.
        </p>
      </div>
    </AppShell>
  );
}


function ActionItem({ children, to, onClick, disabled, danger, newTab, testid }) {
  const style = {
    display: "block", padding: "8px 14px", fontSize: 13,
    color: disabled ? "rgba(27,35,64,0.35)" : danger ? "#B03030" : "var(--m-navy)",
    cursor: disabled ? "default" : "pointer",
    background: "transparent", border: 0, width: "100%", textAlign: "left",
    textDecoration: "none",
  };
  const onEnter = e => { if (!disabled) e.currentTarget.style.background = "rgba(27,35,64,0.05)"; };
  const onLeave = e => { e.currentTarget.style.background = "transparent"; };
  if (disabled) return <div style={style} data-testid={testid}>{children}</div>;
  if (to) return (
    <Link to={to} target={newTab ? "_blank" : undefined}
           rel={newTab ? "noreferrer" : undefined}
           style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}
           data-testid={testid}>{children}</Link>
  );
  return (
    <button type="button" onClick={onClick} style={style}
             onMouseEnter={onEnter} onMouseLeave={onLeave} data-testid={testid}>
      {children}
    </button>
  );
}
